import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import React from "react";
import PropTypes from "prop-types";
import { useUniqueID } from "@xo-union/react-unique-id";
import SignUpForm from "../SignUpForm/index.js";
import LogInForm from "../LogInForm/index.js";
import CanSpam from "../CanSpam/index.js";
const LOG_IN = 'LOG_IN';
const SIGN_UP = 'SIGN_UP';
const MembershipForm = /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    formType,
    inProgress,
    initialFocusRef,
    metadata,
    onClickLogIn,
    onClickSignUp,
    onLogIn,
    onSignUp,
    emailProps: givenEmailProps,
    passwordProps: givenPasswordProps,
    alertProps: givenAlertProps,
    SignUpView,
    LogInView,
    logInFormProps,
    signUpFormProps,
    // Deprecated
    alertID: alertIDProp,
    children
  } = _ref;
  const alertIDOverride = givenAlertProps.id || alertIDProp;
  const alertID = useUniqueID(alertIDOverride);
  const staticAlertProps = { ...givenAlertProps,
    id: alertID
  };
  const [alertProps, setAlertProps] = React.useState({
    type: 'error',
    visible: false
  });
  const describedById = alertProps.visible ? alertID : null;
  const fieldProps = {
    'aria-describedby': describedById
  };
  const emailProps = { ...fieldProps,
    ...givenEmailProps
  };
  const passwordProps = { ...fieldProps,
    ...givenPasswordProps
  };

  const dismissError = () => {
    setAlertProps(curProps => ({ ...curProps,
      visible: false
    }));
  };

  React.useImperativeHandle(ref, () => ({
    showError: data => {
      setAlertProps(curProps => ({ ...curProps,
        data,
        visible: true
      }));
    },
    dismissError
  }), []);
  React.useEffect(() => dismissError(), [formType]);
  React.useEffect(() => {
    /* eslint-disable no-console */
    if (typeof alertIDProp !== 'undefined') {
      console.warn('MembershipForm: Please use `alertProps.id` instead of `alertID`');
    }

    if (typeof children !== 'undefined') {
      console.warn('MembershipForm: children prop is deprecated. Either stop using `alertWrapper` or stop passing `children` in favor of sibling elements.');
    }
  }, [alertIDProp, children]);
  return /*#__PURE__*/React.createElement(CanSpam, null, formType === SIGN_UP && /*#__PURE__*/React.createElement(SignUpView, _extends({
    onClickLogIn: onClickLogIn,
    onSubmit: onSignUp,
    metadata: metadata,
    inProgress: inProgress,
    initialFocusRef: initialFocusRef,
    emailProps: emailProps,
    passwordProps: passwordProps,
    alertProps: { ...staticAlertProps,
      ...alertProps
    }
  }, signUpFormProps)), formType === LOG_IN && /*#__PURE__*/React.createElement(LogInView, _extends({
    onClickSignUp: onClickSignUp,
    onSubmit: onLogIn,
    metadata: metadata,
    inProgress: inProgress,
    initialFocusRef: initialFocusRef,
    emailProps: emailProps,
    passwordProps: passwordProps,
    alertProps: { ...staticAlertProps,
      ...alertProps
    }
  }, logInFormProps)), children);
});
process.env.NODE_ENV !== "production" ? MembershipForm.propTypes = {
  /**
   * Name of form
   */
  formType: PropTypes.oneOf([SIGN_UP, LOG_IN]).isRequired,

  /**
   * Callback called when clicking on the Log In link
   */
  onClickLogIn: PropTypes.func.isRequired,

  /**
   * Callback called when clicking on the Sign Up link
   */
  onClickSignUp: PropTypes.func.isRequired,

  /**
   * Callback called when the Sign Up form is submitted
   */
  onSignUp: PropTypes.func.isRequired,

  /**
   * Callback called when the Log In form is submitted
   */
  onLogIn: PropTypes.func.isRequired,

  /**
   * Metadata about user action necessary for sign up form
   */
  metadata: PropTypes.shape({
    userAction: PropTypes.string.isRequired,
    applicationName: PropTypes.string.isRequired
  }).isRequired,

  /**
   * Whether or not we are waiting for a membership response. Used to render the "loading" state of
   * the form
   */
  inProgress: PropTypes.bool,

  /**
   * Component to use as sign up view. Will default to SignUpForm.
   */
  SignUpView: PropTypes.func,

  /**
   * Component to use as log in view. Will default to LogInForm.
   */
  LogInView: PropTypes.func,

  /**
   * Element to set as the initial focus
   */
  initialFocusRef: PropTypes.shape({
    current: PropTypes.shape({})
  }),

  /**
   * Used to set additional props on the email field
   */
  emailProps: PropTypes.shape(),

  /**
   * Used to set additional props on the password field
   */
  passwordProps: PropTypes.shape(),

  /**
   * Used to set additional props on the alert
   */
  alertProps: PropTypes.shape(),

  /**
   * Props to pass to sign up form
   */
  signUpFormProps: PropTypes.shape(),

  /**
   * Props to pass to log in form
   */
  logInFormProps: PropTypes.shape(),

  /**
   * @deprecated
   *
   * Override the alert ID. Prefer using `alertProps.id`
   */
  alertID: PropTypes.string,

  /**
   * @deprecated
   *
   * This is leveraged by the `alertWrapper` HOC which is also deprecated.
   */
  children: PropTypes.node
} : void 0;
MembershipForm.defaultProps = {
  inProgress: false,
  SignUpView: SignUpForm,
  LogInView: LogInForm,
  emailProps: {},
  passwordProps: {},
  alertProps: {}
};
MembershipForm.displayName = 'MembershipForm';
export default MembershipForm;